import { Component, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChildren, inject } from '@angular/core';
import { DateIntervalFilterData } from '../../../../../_shared/models';
import { CommonModule } from '@angular/common';
import { EmptyStateComponent } from "../../../../../_shared/components/states/empty-state/empty-state.component";
import { SortHeaderComponent } from "../../../../../_shared/components/sort-header/sort-header.component";
import { NgbDropdown, NgbDropdownModule, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { SearchComponent } from "../../../../../_shared/components/filters/search/search.component";
import { UsageForecastComponent } from "../usage-forecast/usage-forecast.component";
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { LookupService } from '../../../../_shared/services/lookup.service';
import { LookupPartCategory, LookupWarehouse } from '../../../../_shared/models/lookup.model';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { EditPartComponent } from '../modals/edit-part/edit-part.component';
import { PartsAndInventoryService } from '../../services/parts-and-inventory.service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationModalComponent } from '../../../../../_shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationOptions } from '../../../../../_shared/models/modals/confirmation-options.model';
import { AddStockComponent } from '../modals/add-stock/add-stock.component';
import { ReturnPartsComponent } from '../modals/return-parts/return-parts.component';
import { PartOverviewComponent } from '../modals/part-overview/part-overview.component';
import { FilterOption, PartInventoryItem, PartInventorySortColumn, SortDirection } from '../../models/inventory-overview.model';
import { StockStatus } from '../../enums/stock-status.enum';
import { take } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-inventory-overview',
  standalone: true,
  imports: [CommonModule, EmptyStateComponent, SortHeaderComponent, NgbDropdownModule, NgbPopoverModule, SearchComponent, UsageForecastComponent, InfiniteScrollModule, TranslocoDirective],
  templateUrl: './inventory-overview.component.html',
  styleUrl: './inventory-overview.component.scss'
})
export class InventoryOverviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() accountIds: number[] = [];
  @Input() accountsLoaded: boolean = false;
  @Input() refreshTrigger: number = 0;

  @ViewChildren(NgbDropdown) dropdowns!: QueryList<NgbDropdown>;
  @Input() dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;

  private readonly selectedWarehouseTooltipThreshold = 18;
  private readonly warehouseItemTooltipThreshold = 34;
  private readonly locationTooltipThreshold = 14;
  private readonly partNumberTooltipThreshold = 14;
  private readonly partNameTooltipThreshold = 22;
  private readonly pageSize: number = 10;

  private errorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private lookupService = inject(LookupService);
  private modalService = inject(NgbModal);
  private partsAndInventoryService = inject(PartsAndInventoryService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  displayedItems: PartInventoryItem[] = [];
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  isWarehousesLoading: boolean = false;
  isCategoriesLoading: boolean = false;
  hasInitialLoadCompleted: boolean = false;

  public searchTerm: string = '';
  public selectedWarehouseId: number | null = null;
  public selectedCategoryId: number | null = null;
  public selectedStockStatus: StockStatus | null = null;
  public warehouseSearchTerm: string = '';
  public sortColumn: PartInventorySortColumn | null = null;
  public sortDirection: SortDirection = null;

  public warehouseOptions: FilterOption[] = [{ id: null, name: this.transloco.translate('parts.inventory.filters.allWarehouses') }];
  public categoryOptions: FilterOption[] = [{ id: null, name: this.transloco.translate('parts.inventory.filters.allCategory') }];
  public stockStatusOptions: FilterOption[] = [
    { id: null, name: this.transloco.translate('parts.inventory.filters.allStock') },
    { id: StockStatus.OutOfStock, name: this.transloco.translate('parts.inventory.filters.outOfStock') },
    { id: StockStatus.LowStock, name: this.transloco.translate('parts.inventory.filters.lowStock') },
    { id: StockStatus.Overstock, name: this.transloco.translate('parts.inventory.filters.overstock') }
  ];

  private currentPage: number = 1;
  private totalPages: number = 0;
  private hasMoreData: boolean = true;
  private requestSequence: number = 0;
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.isLoading = true;

    if (this.accountsLoaded) {
      this.initializePage();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accountsLoaded'] && this.accountsLoaded && !this.hasInitialLoadCompleted) {
      this.isLoading = true;
      this.initializePage();
      return;
    }

    if (changes['accountIds'] && !changes['accountIds'].firstChange && this.accountsLoaded && this.hasInitialLoadCompleted) {
      this.onAccountFilterChanged();
      return;
    }

    if (changes['refreshTrigger'] && !changes['refreshTrigger'].firstChange && this.accountsLoaded && this.hasInitialLoadCompleted) {
      void this.resetAndLoadInventoryAsync();
    }
  }

  ngOnDestroy(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  }

  private async initializePage(): Promise<void> {
    try {
      await Promise.all([
        this.loadWarehouses(false),
        this.loadCategories(false)
      ]);

      await this.resetAndLoadInventoryAsync();
    } finally {
      this.hasInitialLoadCompleted = true;
    }
  }

  private async onAccountFilterChanged(): Promise<void> {
    await this.loadWarehouses(false);
    await this.resetAndLoadInventoryAsync();
  }

  private async resetAndLoadInventoryAsync(): Promise<void> {
    this.currentPage = 1;
    this.totalPages = 0;
    this.hasMoreData = true;
    this.displayedItems = [];

    await this.loadInventoryPageAsync(true);
  }

  private async loadInventoryPageAsync(isInitialLoad: boolean): Promise<void> {
    if (!this.accountsLoaded) {
      this.isLoading = false;
      this.isLoadingMore = false;
      return;
    }

    if (!isInitialLoad && (!this.hasMoreData || this.isLoadingMore)) {
      return;
    }

    const requestId = ++this.requestSequence;

    if (isInitialLoad) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }

    try {
      const response = await this.partsAndInventoryService.getPaginatedPartsInventoryAsync({
        page: this.currentPage,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm?.trim() ?? '',
        accountIds: this.accountIds ?? [],
        warehouseIds: this.selectedWarehouseId ? [this.selectedWarehouseId] : [],
        categoryIds: this.selectedCategoryId ? [this.selectedCategoryId] : [],
        stockStatus: this.selectedStockStatus,
        sortColumn: this.sortColumn ?? undefined,
        sortDirection: this.sortDirection ?? undefined
      });

      if (requestId !== this.requestSequence) {
        return;
      }

      const data = response.data;
      const mappedResults = data?.results ?? [];

      this.displayedItems = isInitialLoad ? mappedResults : [...this.displayedItems, ...mappedResults];
      this.totalPages = data?.totalPages ?? 0;
      this.hasMoreData = this.currentPage < this.totalPages;

      if (this.hasMoreData) {
        this.currentPage += 1;
      }
    } catch (error: any) {
      if (requestId === this.requestSequence) {
        this.errorHandlerService.handleError(error);
      }
    } finally {
      if (requestId === this.requestSequence) {
        if (isInitialLoad) {
          this.isLoading = false;
        } else {
          this.isLoadingMore = false;
        }
      }
    }
  }

  loadMoreItems(): void {
    void this.loadInventoryPageAsync(false);
  }

  onScrollDown(): void {
    if (this.hasMoreData && !this.isLoadingMore) {
      this.loadMoreItems();
    }
  }

  isReorderPointReached(item: PartInventoryItem): boolean {
    return item.onHand <= item.reorderPoint;
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.searchDebounceTimer = setTimeout(() => {
      void this.resetAndLoadInventoryAsync();
    }, 300);
  }

  onWarehouseFilterChange(warehouse: FilterOption): void {
    this.selectedWarehouseId = warehouse.id;
    this.warehouseSearchTerm = '';
    void this.resetAndLoadInventoryAsync();
  }

  onCategoryFilterChange(category: FilterOption): void {
    this.selectedCategoryId = category.id;
    void this.resetAndLoadInventoryAsync();
  }

  onStockStatusFilterChange(option: FilterOption): void {
    this.selectedStockStatus = option.id as StockStatus | null;
    void this.resetAndLoadInventoryAsync();
  }

  onSort(column: string): void {
    const nextDirection: SortDirection =
      this.sortColumn !== column || !this.sortDirection
        ? 'asc'
        : this.sortDirection === 'asc'
          ? 'desc'
          : null;

    this.sortColumn = nextDirection ? (column as PartInventorySortColumn) : null;
    this.sortDirection = nextDirection;
    void this.resetAndLoadInventoryAsync();
  }

  private async loadWarehouses(reloadInventoryIfSelectionChanged: boolean = true): Promise<void> {
    this.isWarehousesLoading = true;
    try {
      const response = await this.lookupService.getWarehousesAsync(this.accountIds ?? []);
      const options = (response.data ?? []).map((w: LookupWarehouse) => ({ id: w.id, name: w.name }));
      this.warehouseOptions = [{ id: null, name: this.transloco.translate('parts.inventory.filters.allWarehouses') }, ...options];
      this.warehouseSearchTerm = '';

      const isCurrentSelectionValid = this.selectedWarehouseId === null || options.some(item => item.id === this.selectedWarehouseId);
      if (!isCurrentSelectionValid) {
        this.selectedWarehouseId = null;
        if (reloadInventoryIfSelectionChanged) {
          await this.resetAndLoadInventoryAsync();
        }
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
      this.warehouseOptions = [{ id: null, name: this.transloco.translate('parts.inventory.filters.allWarehouses') }];
      this.selectedWarehouseId = null;
    } finally {
      this.isWarehousesLoading = false;
    }
  }

  private async loadCategories(reloadInventoryIfSelectionChanged: boolean = true): Promise<void> {
    this.isCategoriesLoading = true;
    try {
      const response = await this.lookupService.getPartCategoriesAsync();
      const options = (response.data ?? []).map((category: LookupPartCategory) => ({ id: category.id, name: category.name }));
      this.categoryOptions = [{ id: null, name: this.transloco.translate('parts.inventory.filters.allCategory') }, ...options];

      const isCurrentSelectionValid = this.selectedCategoryId === null || options.some(item => item.id === this.selectedCategoryId);
      if (!isCurrentSelectionValid) {
        this.selectedCategoryId = null;
        if (reloadInventoryIfSelectionChanged) {
          await this.resetAndLoadInventoryAsync();
        }
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
      this.categoryOptions = [{ id: null, name: this.transloco.translate('parts.inventory.filters.allCategory') }];
      this.selectedCategoryId = null;
    } finally {
      this.isCategoriesLoading = false;
    }
  }

  get shouldShowWarehouseSearch(): boolean {
    return (this.warehouseOptions.length - 1) > 5;
  }

  get filteredWarehouseOptions(): FilterOption[] {
    const query = this.warehouseSearchTerm.trim().toLowerCase();
    if (!query) {
      return this.warehouseOptions;
    }

    const allOption = this.warehouseOptions.find(item => item.id === null);
    const filtered = this.warehouseOptions.filter(item => item.id !== null && item.name.toLowerCase().includes(query));
    return allOption ? [allOption, ...filtered] : filtered;
  }

  get selectedWarehouseLabel(): string {
    if (this.isWarehousesLoading) {
      return this.transloco.translate('parts.inventory.filters.loadingWarehouses');
    }

    return this.warehouseOptions.find(item => item.id === this.selectedWarehouseId)?.name
      ?? this.transloco.translate('parts.inventory.filters.allWarehouses');
  }

  get selectedCategoryLabel(): string {
    if (this.isCategoriesLoading) {
      return this.transloco.translate('parts.inventory.filters.loadingCategories');
    }

    return this.categoryOptions.find(item => item.id === this.selectedCategoryId)?.name
      ?? this.transloco.translate('parts.inventory.filters.allCategory');
  }

  get selectedStockStatusLabel(): string {
    return this.stockStatusOptions.find(item => item.id === this.selectedStockStatus)?.name
      ?? this.transloco.translate('parts.inventory.filters.allStock');
  }

  getSelectedWarehouseTooltip(): string | null {
    const value = this.selectedWarehouseLabel;
    return this.shouldShowTooltip(value, this.selectedWarehouseTooltipThreshold) ? value : null;
  }

  getWarehouseItemTooltip(warehouseName: string): string | null {
    return this.shouldShowTooltip(warehouseName, this.warehouseItemTooltipThreshold) ? warehouseName : null;
  }

  getLocationTooltip(location: string | null | undefined): string | null {
    if (!location) {
      return null;
    }

    return this.shouldShowTooltip(location, this.locationTooltipThreshold) ? location : null;
  }

  getPartNumberTooltip(partNumber: string | null | undefined): string | null {
    if (!partNumber) {
      return null;
    }

    return this.shouldShowTooltip(partNumber, this.partNumberTooltipThreshold) ? partNumber : null;
  }

  getPartNameTooltip(item: PartInventoryItem): string | null {
    if (!item?.partName) {
      return null;
    }

    return this.shouldShowTooltip(item.partName, this.partNameTooltipThreshold) ? item.partName : null;
  }

  private shouldShowTooltip(value: string, threshold: number): boolean {
    return !!value && value.trim().length > threshold;
  }

  private refreshKpiWidgets(): void {
    this.filterService.updateFilter({ ...this.filterService.filter });
  }

  async onDeleteClick(item: PartInventoryItem, event: MouseEvent) {
    event.stopPropagation();

    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
    };

    const modalRef = this.modalService.open(ConfirmationModalComponent, modalOptions);
    modalRef.componentInstance.data = {
      title: this.transloco.translate('parts.inventory.delete.title'),
      message: this.transloco.translate('parts.inventory.delete.message'),
      warningMessage: this.transloco.translate('parts.inventory.delete.warning'),
      confirmationPrompt: 'delete',
      confirmPlaceholder: this.transloco.translate('parts.inventory.delete.confirmPlaceholder'),
      confirmText: this.transloco.translate('parts.inventory.delete.confirmText'),
      declineText: this.transloco.translate('parts.inventory.delete.declineText'),
      onConfirm: async () => {
        await this.partsAndInventoryService.deletePartByIdAsync(item.partId);
        this.toastr.success(this.transloco.translate('parts.inventory.toasts.deleted'));
      },
      closeOnSuccess: true
    } as ConfirmationOptions;

    modalRef.closed.pipe(take(1)).subscribe((result) => {
      if (result === true) {
        void this.reloadInventoryAndKpisAfterDelete();
      }
    });
  }

  private async reloadInventoryAndKpisAfterDelete(): Promise<void> {
    await this.resetAndLoadInventoryAsync();
    this.refreshKpiWidgets();
  }

  onReturnPartsClick(item: PartInventoryItem, event: MouseEvent) {
    event.stopPropagation();

    const modalRef = this.modalService.open(ReturnPartsComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'return-parts-modal-window'
    });

    modalRef.componentInstance.partId = item.partId;

    modalRef.result
      .then((result: boolean) => {
        if (result === true) {
          void this.resetAndLoadInventoryAsync();
        }
      })
      .catch(() => { });
  }

  onAddStockClick(item: PartInventoryItem, event: MouseEvent) {
    event.stopPropagation();

    const modalRef = this.modalService.open(AddStockComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'add-stock-modal-window'
    });

    modalRef.componentInstance.partId = item.partId;

    modalRef.result
      .then((result: boolean) => {
        if (result === true) {
          void this.resetAndLoadInventoryAsync();
        }
      })
      .catch(() => { });
  }

  openPartOverview(item: PartInventoryItem): void {
    const modalRef = this.modalService.open(PartOverviewComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'part-overview-modal-window'
    });

    modalRef.componentInstance.partId = item.partId;
    modalRef.componentInstance.accountIds = this.accountIds ?? [];
    modalRef.componentInstance.dateFilter = this.dateFilter;
    modalRef.componentInstance.inventoryItem = item;

    modalRef.result
      .then((result: boolean) => {
        if (result === true) {
          void this.resetAndLoadInventoryAsync();
        }
      })
      .catch(() => { });
  }

  onEditClick(item: PartInventoryItem, event: MouseEvent) {
    event.stopPropagation();

    const modalRef = this.modalService.open(EditPartComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'edit-part-modal-window'
    });

    modalRef.componentInstance.partId = item.partId;

    modalRef.result
      .then((result: boolean) => {
        if (result === true) {
          void this.resetAndLoadInventoryAsync();
        }
      })
      .catch(() => { });
  }

  onTableScroll(): void {
    this.dropdowns.forEach(d => d.close());
  }
}